// @flow
import * as React from 'react';
import { ProviderOutput } from '@coverright/data-access/types/medicare';
import { ProviderAddressOutput } from '@coverright/data-access/types/medicare';

import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { Box, Button, Checkbox, Divider, FormControlLabel, Skeleton } from '@mui/material';
import { handleCollectionChange } from '@coverright/utils/utils/utils';
import { DoctorsDrugsItemDescription } from '../doctors-drugs-item';
import { Stethoscope } from '@phosphor-icons/react';
import { useEffect, useMemo } from 'react';
import { useFindProviderAddressesByNpiQuery } from '@coverright/data-access/medicare/documents';

type Props = {
  doctor: ProviderOutput;
  onSelect: (addresses: ProviderAddressOutput[]) => void;
  specialty?: string;
  addDoctorButtonLabel: string
  onCancel: () => void;
};

export function DoctorSelectionModalAddress(props: Props) {
  const [selected, setSelected] = React.useState(
    props.doctor.addresses.map((a) => a.id)
  );

  const { data } = useFindProviderAddressesByNpiQuery({
    skip: !props.doctor.npi,
    variables: { npi: props.doctor.npi },
  });

  const addresses = useMemo(() => data?.findProviderAddressesByNpi || [], [data]);

  useEffect(() => {
    if (addresses.length === 1) {
      setSelected(addresses.map(a => a.id));
    }
  }, [addresses]);

  const handleAddDoctorClick = () => {
    props.onSelect(addresses.filter((a) => selected.includes(a.id)));
  };

  return <>
    <DoctorsDrugsItemDescription icon={<Stethoscope size={24} color={'#216069'} />}
                                 title={props.doctor.name}
                                 subtitle={props.specialty} />

    <Divider sx={{ mt: 2, mb: 1 }} />

    {addresses?.length === 0 && <>
      <AddressSkeleton />
      <AddressSkeleton />
    </>}

    {addresses?.length > 0 && (
      <OverlayScrollbarsComponent
        style={{maxHeight: 400}}
        defer
      >
        {addresses.map(address => (
          <FormControlLabel control={<Checkbox checked={selected.includes(address.id)}/>}
                            onChange={() => {
                              setSelected((prev) =>
                                handleCollectionChange(address.id, prev)
                              );
                            }}
                            key={address.id}
                            label={[address.addressLine1, address.addressLine2].join(', ')} />
        ))}
      </OverlayScrollbarsComponent>
    )}

    <Box sx={{display: 'flex', mt: 3, gap: 1.5, alignItems: 'center', justifyContent: 'flex-end'}}>
      <Button size={'large'} variant={'outlined'} onClick={props.onCancel}>Select other doctor</Button>
      <Button size={'large'} variant={'contained'} onClick={handleAddDoctorClick} disabled={!selected.length}>{props.addDoctorButtonLabel}</Button>
    </Box>
  </>;
}

const AddressSkeleton = () => <Box sx={{display: 'flex', alignItems: 'center', width: 1, gap: 1, height: 42}}>
  <Skeleton variant={'rectangular'} width={20} height={20} />
  <Skeleton width={400} />
</Box>
